import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseService';
import { FileText, Upload, Save, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';

interface WordTemplateSettingsTabProps {
    clientId: string;
}

export const WordTemplateSettingsTab: React.FC<WordTemplateSettingsTabProps> = ({ clientId }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [templateBase64, setTemplateBase64] = useState<string | null>(null);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {
        fetchTemplate();
    }, [clientId]);

    const fetchTemplate = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('clients')
                .select('word_template_url')
                .eq('id', clientId)
                .single();

            if (error) throw error;
            if (data?.word_template_url) {
                setTemplateBase64(data.word_template_url);
            }
        } catch (e: any) {
            console.error("Failed to load template", e);
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.name.endsWith('.docx')) {
            setErrorMsg("Alleen .docx bestanden zijn toegestaan.");
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            const result = event.target?.result as string;
            // result is a data URL (e.g. data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,... )
            setTemplateBase64(result);
            setErrorMsg('');
        };
        reader.onerror = () => {
            setErrorMsg("Kon het bestand niet inlezen.");
        };
        reader.readAsDataURL(file);
    };

    const handleSave = async () => {
        if (!templateBase64) return;
        setSaving(true);
        setErrorMsg('');
        setSuccessMsg('');

        try {
            const { error } = await supabase
                .from('clients')
                .update({ word_template_url: templateBase64 })
                .eq('id', clientId);

            if (error) throw error;
            setSuccessMsg("Word CV template is succesvol opgeslagen! Je kunt nu CV's genereren in het kandidaatprofiel.");
            setTimeout(() => setSuccessMsg(''), 5000);
        } catch (e: any) {
            console.error(e);
            setErrorMsg("Er ging iets mis bij het opslaan: " + e.message);
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async () => {
        if (!confirm("Weet je zeker dat je de template wilt verwijderen?")) return;
        setSaving(true);
        try {
            const { error } = await supabase
                .from('clients')
                .update({ word_template_url: null })
                .eq('id', clientId);

            if (error) throw error;
            setTemplateBase64(null);
            setSuccessMsg("Template succesvol verwijderd.");
            setTimeout(() => setSuccessMsg(''), 5000);
        } catch (e: any) {
            console.error(e);
            setErrorMsg("Verwijderen mislukt.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-8 flex items-center justify-center"><Loader2 className="animate-spin text-indigo-500" /></div>;

    return (
        <div className="max-w-3xl">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-200 bg-slate-50 flex items-center gap-3">
                    <div className="w-10 h-10 bg-red- rounded-xl flex items-center justify-center text-red- shadow-inner">
                        <FileText size={20} />
                    </div>
                    <div>
                        <h2 className="text-xl font-black text-slate-900 tracking-tight">Word 365 CV Template</h2>
                        <p className="text-sm text-slate-500 mt-1 font-medium">Upload een .docx bestand met tags zoals {'{{name}}'} en {'{{role}}'} om automatisch CV's te genereren vanuit Werftools direct naar Word Online.</p>
                    </div>
                </div>

                <div className="p-6 space-y-6">
                    {successMsg && (
                        <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl text-sm font-semibold text-emerald-700 flex items-start gap-3">
                            <CheckCircle2 size={18} className="text-emerald-500 shrink-0 mt-0.5" />
                            <p>{successMsg}</p>
                        </div>
                    )}

                    {errorMsg && (
                        <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-sm font-semibold text-red-700 flex items-start gap-3">
                            <AlertCircle size={18} className="text-red-500 shrink-0 mt-0.5" />
                            <p>{errorMsg}</p>
                        </div>
                    )}

                    <div className="space-y-4">
                        <label className="text-sm font-bold text-slate-700 block">Jouw Template (.docx)</label>

                        {!templateBase64 ? (
                            <div className="border-2 border-dashed border-slate-300 rounded-xl p-10 text-center hover:bg-slate-50 transition-colors relative cursor-pointer">
                                <input type="file" accept=".docx" onChange={handleFileUpload} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                                <div className="flex flex-col items-center justify-center pointer-events-none">
                                    <div className="w-12 h-12 bg-white rounded-full shadow-sm flex items-center justify-center mb-3">
                                        <Upload size={24} className="text-red-0" />
                                    </div>
                                    <p className="text-sm font-bold text-slate-800">Klik of sleep een map om te uploaden</p>
                                    <p className="text-xs font-medium text-slate-500 mt-1">Alleen Microsoft Word .docx bestanden</p>
                                </div>
                            </div>
                        ) : (
                            <div className="border border-slate-200 rounded-xl p-4 flex items-center justify-between bg-slate-50">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-white shadow-sm border border-slate-100 rounded flex items-center justify-center text-red-">
                                        <FileText size={20} />
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-slate-800">Huidige Template Actief</p>
                                        <p className="text-xs text-slate-500 font-medium">Klaar om gebruikt te worden voor CV generatie.</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="relative overflow-hidden inline-block cursor-pointer">
                                        <button className="px-3 py-1.5 text-xs font-bold bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer">
                                            Vervang
                                        </button>
                                        <input type="file" accept=".docx" onChange={handleFileUpload} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                                    </div>
                                    <button onClick={handleRemove} className="px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                                        Verwijder
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="pt-6 border-t border-slate-100 flex justify-end">
                        <button
                            onClick={handleSave}
                            disabled={saving || !templateBase64}
                            className="bg-slate-900 hover:bg-black text-white px-6 py-2.5 rounded-xl text-sm font-bold shadow-sm transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                            {saving ? 'Bezig met opslaan...' : 'Wijzigingen Opslaan'}
                        </button>
                    </div>

                    <div className="bg-red- rounded-xl p-5 border border-red- mt-8">
                        <h4 className="text-sm font-bold text-red- flex items-center gap-2 mb-3">
                            <FileText size={16} /> Beschikbare Tags voor je document
                        </h4>
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                            {['{{name}}', '{{role}}', '{{email}}', '{{phone}}', '{{location}}', '{{summary}}', '{{skills}}', '{{education}}', '{{workHistory}}'].map(tag => (
                                <div key={tag} className="text-xs font-mono bg-white border border-red- text-red- px-3 py-1.5 rounded-lg shadow-sm">
                                    {tag}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
